import { Observer } from 'mobx-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import ScriptManagementStore from '../stores/ScriptManagementStore';
import { Button, Layout, Popconfirm, Space, Table, Tooltip, Tag } from 'antd';
import LayoutHeader from '../../../components/LayoutHeader';
import { Content } from 'antd/lib/layout/layout';
import { Utils } from '../../common/services/Utils';
import { Script, UsageInProjects } from '../types';

type Props = {
    store: ScriptManagementStore;
};

const RemainingScriptUsages: React.FC<{ usages: UsageInProjects[]; scriptId: string }> = ({ usages, scriptId }) => {
    const [visible, setVisible] = React.useState(false);
    const tooltipId = `script-usage-remaining-${scriptId}`;

    React.useEffect(() => {
        if (!visible) {
            return;
        }

        const dismissOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setVisible(false);
            }
        };

        document.addEventListener('keydown', dismissOnEscape);
        return () => document.removeEventListener('keydown', dismissOnEscape);
    }, [visible]);

    return (
        <Tooltip
            // Ant Design forwards forceRender to rc-trigger, but its installed Tooltip types omit it.
            {...{ forceRender: true }}
            id={tooltipId}
            trigger={['hover', 'focus']}
            visible={visible}
            onVisibleChange={setVisible}
            overlayInnerStyle={{ maxHeight: 300, overflowY: 'auto' }}
            title={usages.map(usage => (
                <div key={usage.projectId}>{usage.projectName}</div>
            ))}
        >
            <button
                type="button"
                aria-describedby={tooltipId}
                onClick={() => setVisible(true)}
                style={{
                    flexShrink: 0,
                    marginLeft: 8,
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    color: 'inherit',
                    font: 'inherit',
                    cursor: 'pointer'
                }}
            >
                + {usages.length} {usages.length === 1 ? 'other' : 'others'}
            </button>
        </Tooltip>
    );
};

const ScriptUsageCell: React.FC<{ store: ScriptManagementStore; scriptId: string }> = observer(
    ({ store, scriptId }) => {
        const usages = store.getScriptUsages(scriptId);

        if (store.isLoadingUsagesInProjects) {
            return <span style={{ color: '#999' }}>Loading...</span>;
        }

        if (store.hasUsagesInProjectsError) {
            return <span style={{ color: '#999' }}>Usage unavailable</span>;
        }

        if (usages.length === 0) {
            return <span style={{ color: '#999' }}>Not used</span>;
        }

        const [firstUsage, ...remainingUsages] = usages;

        return (
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%', whiteSpace: 'nowrap' }}>
                <Tooltip title={firstUsage.projectName}>
                    <Tag
                        color="blue"
                        style={{
                            width: 120,
                            flexShrink: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginRight: 0
                        }}
                    >
                        {firstUsage.projectName}
                    </Tag>
                </Tooltip>
                {remainingUsages.length > 0 && <RemainingScriptUsages usages={remainingUsages} scriptId={scriptId} />}
            </div>
        );
    }
);

const ScriptManagementList: React.FC<Props> = ({ store }) => {
    React.useEffect(() => {
        if (store.scripts.length === 0) {
            store.loadScripts();
        }
    }, [store]);

    React.useEffect(() => {
        if (store.scripts.length > 0) {
            store.loadScriptUsagesInProjects();
        }
    }, [store, store.scripts]);

    const columns = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => <span style={{ color: '#999', fontSize: '0.8rem' }}>{id}</span>
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: Script, b: Script) => a.name.localeCompare(b.name)
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: 'Created At',
            dataIndex: 'createDate',
            key: 'createDate',
            render: (date: string) => <span style={{ color: '#999' }}>{Utils.formatDateStringShort(date)}</span>,
            sorter: (a: Script, b: Script) => Utils.safeDateStringCompare(a.createDate, b.createDate)
        },
        {
            title: 'Updated At',
            dataIndex: 'updateDate',
            key: 'updateDate',
            render: (date: string | undefined) => (
                <span style={{ color: '#999' }}>{date ? Utils.formatDateStringShort(date) : '-'}</span>
            ),
            sorter: (a: Script, b: Script) => Utils.safeDateStringCompare(a.updateDate, b.updateDate)
        },
        {
            title: 'Changed By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            render: (_: unknown, record: Script) => {
                const userId = record.updatedBy || record.createdBy;
                return <Observer>{() => <>{store.getUserNameById(userId)}</>}</Observer>;
            },
            sorter: (a: Script, b: Script) => {
                const aUserId = a.updatedBy || a.createdBy || '';
                const bUserId = b.updatedBy || b.createdBy || '';
                return aUserId.localeCompare(bUserId);
            }
        },
        {
            title: 'Script Usage',
            key: 'usage',
            width: 260,
            render: (_: unknown, record: Script) => <ScriptUsageCell store={store} scriptId={record.id} />
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_: unknown, record: Script) => (
                <div className="row-actions-wrapper">
                    <Space size="middle">
                        <Tooltip title="Edit script">
                            <Button
                                type="link"
                                size="small"
                                data-id="button-edit-script"
                                icon={<i className="alpha-icon lg edit-icon" />}
                                onClick={() => store.navigateToEditScript(record.id)}
                            />
                        </Tooltip>
                        <Popconfirm
                            title="Are you sure you want to delete this script?"
                            onConfirm={() => store.deleteScript(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Tooltip title="Delete script">
                                <Button
                                    type="link"
                                    size="small"
                                    data-id="button-delete-script"
                                    icon={<i className="alpha-icon lg delete-icon" />}
                                    danger
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                </div>
            )
        }
    ];

    return (
        <Layout style={{ height: '100%', background: 'white' }}>
            <LayoutHeader
                title="Script Management"
                buttons={[
                    <Button
                        key="create-script"
                        data-id="button-create-script"
                        type="primary"
                        size="large"
                        onClick={() => store.navigateToCreateScript()}
                    >
                        Create
                    </Button>
                ]}
            />
            <Layout className="script-management-list">
                <Content style={{ maxHeight: 'calc(100vh - 70px)', overflowY: 'auto' }}>
                    <Table
                        data-id="scripts-table"
                        className="alpha-portal-table"
                        dataSource={store.sortedScripts}
                        columns={columns}
                        loading={store.isLoadingScripts || store.isLoadingUsers}
                        rowKey="id"
                        pagination={{
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} scripts`
                        }}
                        sticky
                    />
                </Content>
            </Layout>
        </Layout>
    );
};

export default observer(ScriptManagementList);
